import {
  IconContext,
  type Icon as PhosphorIcon,
  type IconProps,
} from "@phosphor-icons/react";
import type { PropsWithChildren } from "react";

export type { IconProps, PhosphorIcon };

/* The AI21 DS uses Phosphor Icons at outline (regular) weight. Glyphs are
 * imported directly from @phosphor-icons/react by callers (keeps the bundle
 * tree-shakeable) — e.g. `import { PaperPlaneTilt } from "@phosphor-icons/react"`.
 * Button / IconButton accept icons as ReactNode and size them via CSS, so you
 * don't need to set size on each glyph. If production swaps icon sets, this is
 * the one file to change. */

const DS_ICON_CONTEXT: IconProps = {
  weight: "regular",
  size: 20,
  color: "currentColor",
};

/** Sets app-wide Phosphor defaults (regular weight, 20px, currentColor).
 *  Wrap the app root and the Storybook preview with this. */
export function IconProvider({ children }: PropsWithChildren) {
  return (
    <IconContext.Provider value={DS_ICON_CONTEXT}>
      {children}
    </IconContext.Provider>
  );
}

export interface IconComponentProps extends IconProps {
  /** A Phosphor glyph component, e.g. `PaperPlaneTilt`. */
  icon: PhosphorIcon;
}

/** Render a single Phosphor glyph with DS defaults. Prefer passing glyphs
 *  straight to Button/IconButton; use this for one-off icons in custom markup. */
export function Icon({
  icon: Glyph,
  weight = "regular",
  ...props
}: IconComponentProps) {
  return <Glyph weight={weight} {...props} />;
}
